/**
 * The 2D lens: the same eye, composited from baked layers instead of rendered.
 *
 * Why this can get close. The 3D lens is already a layered parallax rig — look
 * at draw() in lens.js: each interior part takes the same gaze vector scaled by
 * its own depth, and the crescents take it inverted. On top of that the rig and
 * hull each get a small lookAt tilt. For a point at depth z a small tilt is,
 * to first order, just more translation, so nearly all of the motion collapses
 * to per-layer translation — which CSS does for free.
 *
 * The layer images are baked out of the real three.js scene, so the shading,
 * the environment reflections and the ACES tone mapping are the genuine
 * article. Only the motion is approximated.
 *
 * Motion budget. JS drives exactly one thing here — the gaze parallax — and the
 * rAF loop SLEEPS the instant the gaze settles on its target. A still eye runs
 * zero frames. The emissive "breathing" is a compositor-only CSS animation (see
 * index.css), so it never touches the main thread, and there is no blend mode,
 * so a moving layer is a pure compositor transform rather than a repaint.
 *
 * The parallax factors are not guesses: core, pupil and arcs were measured by
 * rendering the 3D scene at gaze (0,0) and (1,0) and tracking each layer's
 * luminance centroid; iris and bloom are the analytic values from the same
 * derivation, which agreed with the measured ones to about 1%.
 */

// Feel knobs. FOLLOW is the exponential rate the gaze chases the pointer
// (larger = snappier). AMP scales the baked per-layer parallax factors, i.e.
// how far the eye ranges from centre. Both are pure feel — nudge freely.
const FOLLOW = 30;
const AMP = 1.3;
// First-appearance settle: how long the eye holds dead-centre when it first
// enters view before it starts tracking, so GPU layer promotion lands and it
// never snaps toward the cursor on arrival. mount2dEye is already deferred to
// the first idle frame (index.js `whenIdle`), so the main thread is free by the
// time this runs — a short settle is plenty. It was 700ms and read as a lag.
const SETTLE_MS = 180;

export function mount2dEye(wrap) {
  const layers = [...wrap.querySelectorAll("[data-p]")].map((el) => ({
    el,
    p: parseFloat(el.dataset.p), // px of travel per px of eye size, per unit gaze
  }));
  if (!layers.length) return () => {};

  // Static, but still correct: the layers already sit at neutral gaze, and the
  // CSS breathe is disabled under the same query, so nothing moves.
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
    return () => {};
  }

  // Promote only the layers that actually move, and only now — goLive calls this
  // after the reveal fade. Doing it in static CSS made the fade flatten every
  // layer's buffer each frame.
  for (const l of layers) l.el.style.willChange = "transform";

  // Track the pointer only while it is over the banner, not the whole window. A
  // window-wide listener made the eye chase the far-off cursor (and, with a hard
  // clamp, pin to the disc edge), and reset the idle timer on every stray move.
  const section = wrap.closest(".hal") || window;

  let disposed = false;
  let raf = 0;
  let running = false;
  let inView = false;
  let last = 0;
  let rectDirty = false;

  let rect = wrap.getBoundingClientRect();
  let size = rect.width || 1;

  const gaze = { x: 0, y: 0 };
  const target = { x: 0, y: 0 };
  const EPS = 0.0009; // gaze within this of target counts as settled → sleep

  const paint = () => {
    for (const l of layers) {
      // +y is up in the gaze vector, down in CSS
      const dx = gaze.x * l.p * size * AMP;
      const dy = -gaze.y * l.p * size * AMP;
      l.el.style.transform = `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0)`;
    }
  };

  const loop = (now) => {
    if (!running) return;
    const dt = Math.min(0.05, (now - last) / 1000 || 0.016);
    last = now;
    // Frame-rate-independent exponential smoothing toward the target (Driscoll
    // damping), at the FOLLOW rate set above.
    const k = 1 - Math.exp(-FOLLOW * dt);
    gaze.x += (target.x - gaze.x) * k;
    gaze.y += (target.y - gaze.y) * k;
    paint();

    if (Math.abs(target.x - gaze.x) < EPS && Math.abs(target.y - gaze.y) < EPS) {
      // land exactly on target, then stop — the next target change re-wakes us
      gaze.x = target.x;
      gaze.y = target.y;
      paint();
      running = false;
      raf = 0;
      return;
    }
    raf = requestAnimationFrame(loop);
  };

  // Start the loop if there is somewhere for the gaze to travel. Cheap to call
  // repeatedly: it no-ops while already running or while off screen.
  const wake = () => {
    if (running || disposed || !inView) return;
    running = true;
    last = performance.now();
    raf = requestAnimationFrame(loop);
  };

  const stop = () => {
    running = false;
    if (raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };

  // getBoundingClientRect is a layout read, so we never do it on scroll. Scroll
  // and resize just flag the rect stale; the next pointer move recomputes it
  // once, which is the only moment its exact position matters.
  const refreshRect = () => {
    rect = wrap.getBoundingClientRect();
    size = rect.width || size;
    rectDirty = false;
  };
  const markDirty = () => { rectDirty = true; };

  /* ---- gaze targets: pointer tracking, plus autonomous idle saccades ---- */
  let idleTimer = 0, saccadeTimer = 0, idle = false;

  const scheduleSaccade = (delay) => {
    saccadeTimer = window.setTimeout(() => {
      if (disposed || !idle) return;
      if (rectDirty) refreshRect();
      const a = Math.random() * Math.PI * 2;
      const r = Math.pow(Math.random(), 1.7); // biased small — most darts are gentle
      target.x = Math.cos(a) * r;
      target.y = Math.sin(a) * r * 0.7;
      wake();
      scheduleSaccade(1600 + Math.random() * 2400);
    }, delay);
  };
  const goIdle = () => { if (!idle) { idle = true; scheduleSaccade(400 + Math.random() * 500); } };

  const onMove = (e) => {
    idle = false;
    clearTimeout(saccadeTimer);
    clearTimeout(idleTimer);
    idleTimer = window.setTimeout(goIdle, 2000);
    if (rectDirty) refreshRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Smaller divisor = more sensitive: the eye reaches its range with less
    // pointer movement, so it tracks further for normal cursor motion.
    const norm = Math.max(rect.width, 320) * 0.6;
    const nx = (e.clientX - cx) / norm;
    const ny = -(e.clientY - cy) / norm;
    // Soft radial saturation instead of a hard clamp: a far cursor EASES toward
    // (never snaps to) full deflection, so the eye can't jump to the disc edge.
    const d = Math.hypot(nx, ny);
    const s = d > 1e-4 ? Math.tanh(d) / d : 0;
    target.x = nx * s;
    target.y = ny * s;
    wake();
  };
  const onLeave = () => {
    clearTimeout(idleTimer);
    idleTimer = window.setTimeout(goIdle, 500);
  };

  const listen = (on) => {
    if (on) {
      refreshRect();
      // Pointer events are scoped to the banner, so moving the mouse elsewhere
      // on the page no longer drives the gaze or resets the idle timer.
      section.addEventListener("pointermove", onMove, { passive: true });
      section.addEventListener("pointerleave", onLeave, { passive: true });
      window.addEventListener("scroll", markDirty, { passive: true });
      window.addEventListener("resize", markDirty, { passive: true });
      idleTimer = window.setTimeout(goIdle, 500);
    } else {
      section.removeEventListener("pointermove", onMove);
      section.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("scroll", markDirty);
      window.removeEventListener("resize", markDirty);
      clearTimeout(idleTimer);
      clearTimeout(saccadeTimer);
      idle = false;
    }
  };

  let warmed = false;
  const setInView = (on) => {
    if (on === inView || disposed) return;
    inView = on;
    if (on) {
      // First time the eye appears: hold it dead-centre and still for a brief
      // beat before going interactive, so the GPU promotion (will-change set at
      // setup) lands and it never snaps toward the cursor on arrival. Short,
      // because the parallax only mounts once the thread is already idle.
      if (warmed) {
        listen(true);
      } else {
        warmed = true;
        window.setTimeout(() => { if (!disposed && inView) listen(true); }, SETTLE_MS);
      }
      wake(); // paints the centred gaze; the loop then settles and sleeps
    } else {
      listen(false);
      stop();
    }
  };

  const io = new IntersectionObserver(([e]) => {
    setInView(e.isIntersecting && !document.hidden);
    // A generous top/bottom margin so the layers promote and the settle above
    // elapse while the section is still approaching — by the time it is on
    // screen the eye is composited and already tracking, not promoting mid-view.
  }, { rootMargin: "450px 0px" });
  io.observe(wrap);

  const onVis = () => {
    const r = wrap.getBoundingClientRect();
    setInView(!document.hidden && r.bottom > -120 && r.top < window.innerHeight + 120);
  };
  document.addEventListener("visibilitychange", onVis);

  paint(); // neutral gaze, in case the observer has not fired yet

  return () => {
    disposed = true;
    setInView(false);
    stop();
    io.disconnect();
    document.removeEventListener("visibilitychange", onVis);
  };
}
